var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from "react";
import { Select } from "antd";
import { CompsChart } from "@ms/shared-components";
import { transformToSingleRoomTypeKlineChart, removeParenthesesFromRoomTypeName, } from '../../utils/priceChannelDataTransformer';
import { createLogger } from '../../utils/logger';
import { EmptyDataPlaceholder } from "../EmptyDataPlaceholder";
var isDev = process.env.NODE_ENV === "development";
var getChartSettings = function () {
    return {
        layout: "horizontal",
        tooltip: {
            show: false,
            formatter: function (params) {
                var _a, _b;
                var paramList;
                if (Array.isArray(params))
                    paramList = params;
                else if (params)
                    paramList = [params];
                else
                    paramList = [];
                if (paramList.length === 0)
                    return "";
                var firstParam = paramList[0];
                var channelName = (_b = (_a = firstParam === null || firstParam === void 0 ? void 0 : firstParam.axisValue) !== null && _a !== void 0 ? _a : firstParam === null || firstParam === void 0 ? void 0 : firstParam.name) !== null && _b !== void 0 ? _b : "";
                var candlestickParam = paramList.find(function (item) { return item.seriesType === "candlestick"; });
                if (candlestickParam &&
                    Array.isArray(candlestickParam.value) &&
                    candlestickParam.value.length >= 4) {
                    var klineValue = candlestickParam.value;
                    var low = klineValue[2];
                    var high = klineValue[3];
                    var minPrice = Math.min(low, high);
                    var maxPrice = Math.max(low, high);
                    var result = "".concat(channelName, "<br/>");
                    if (minPrice === maxPrice) {
                        result += "\u4EF7\u683C: ".concat(Math.round(minPrice), " \u5143<br/>");
                    }
                    else {
                        result += "\u6700\u4F4E\u4EF7: ".concat(Math.round(minPrice), " \u5143<br/>");
                        result += "\u6700\u9AD8\u4EF7: ".concat(Math.round(maxPrice), " \u5143<br/>");
                    }
                    return result;
                }
                var fallbackResult = "".concat(channelName, "<br/>");
                paramList.forEach(function (item) {
                    if ((item === null || item === void 0 ? void 0 : item.seriesType) === "line" && Number.isFinite(item.value)) {
                        fallbackResult += "".concat(item.seriesName || "渠道房价", ": ").concat(Math.round(item.value), " \u5143<br/>");
                    }
                });
                return fallbackResult;
            },
        },
    };
};
var ChannelPrice = function (_a) {
    var style = _a.style, storeId = _a.storeId, onTitleClick = _a.onTitleClick, channelPriceData = _a.channelPriceData, titleLeftAdornment = _a.titleLeftAdornment;
    var isAliasPriceQuantityData = useMemo(function () {
        var _a;
        if (!channelPriceData)
            return false;
        return (Array.isArray(channelPriceData.data) &&
            channelPriceData.data.length > 0 &&
            Array.isArray((_a = channelPriceData.data[0]) === null || _a === void 0 ? void 0 : _a.aliases));
    }, [channelPriceData]);
    var roomTypeList = useMemo(function () {
        if (isAliasPriceQuantityData) {
            var aliasData = channelPriceData;
            if (aliasData.data && aliasData.data.length > 0) {
                var allRoomTypes = Array.from(new Set(aliasData.data.flatMap(function (day) {
                    return day.aliases.map(function (alias) { return alias.aliasName; });
                })));
                return allRoomTypes.map(function (name) {
                    return removeParenthesesFromRoomTypeName(name);
                });
            }
        }
        else if (channelPriceData &&
            "data" in channelPriceData &&
            channelPriceData.data &&
            !Array.isArray(channelPriceData.data) &&
            "roomTypeList" in channelPriceData.data) {
            var priceChannelData = channelPriceData;
            return priceChannelData.data.roomTypeList.map(function (rt) {
                return removeParenthesesFromRoomTypeName(rt.roomTypeName);
            });
        }
        return [];
    }, [channelPriceData, isAliasPriceQuantityData]);
    var _b = useState(""), selectedRoomType = _b[0], setSelectedRoomType = _b[1];
    React.useEffect(function () {
        if (roomTypeList.length > 0 &&
            (!selectedRoomType || !roomTypeList.includes(selectedRoomType))) {
            setSelectedRoomType(roomTypeList[0]);
        }
    }, [roomTypeList, selectedRoomType]);
    var logger = useMemo(function () { return createLogger("ChannelPrice:".concat(storeId)); }, [storeId]);
    var chartData = useMemo(function () {
        var _a, _b, _c, _d;
        if (!channelPriceData) {
            return null;
        }
        if (isAliasPriceQuantityData) {
            logger.warn("检测到日期数据（AliasPriceQuantityResponse），不支持此格式，需要渠道数据");
            return null;
        }
        if (channelPriceData &&
            "data" in channelPriceData &&
            channelPriceData.data &&
            !Array.isArray(channelPriceData.data) &&
            "roomTypeList" in channelPriceData.data) {
            var priceChannelData = channelPriceData;
            var convertedChartData = transformToSingleRoomTypeKlineChart(priceChannelData, selectedRoomType);
            logger.debug("使用 PriceChannelResponse 数据转换为渠道价格K线图", {
                selectedRoomType: selectedRoomType,
                categories: ((_a = convertedChartData === null || convertedChartData === void 0 ? void 0 : convertedChartData.categories) === null || _a === void 0 ? void 0 : _a.length) || 0,
                hasKlineData: !!((_b = convertedChartData === null || convertedChartData === void 0 ? void 0 : convertedChartData.dataset) === null || _b === void 0 ? void 0 : _b.klineData),
                klineDataCount: ((_d = (_c = convertedChartData === null || convertedChartData === void 0 ? void 0 : convertedChartData.dataset) === null || _c === void 0 ? void 0 : _c.klineData) === null || _d === void 0 ? void 0 : _d.length) || 0,
            });
            return convertedChartData;
        }
        logger.warn("数据格式无效，返回 null");
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelPriceData, selectedRoomType, isAliasPriceQuantityData]);
    var roomTypeOptions = roomTypeList.map(function (roomType) { return ({
        label: roomType,
        value: roomType,
    }); });
    var hasValidData = channelPriceData && chartData && roomTypeList.length > 0;
    var diagRaw = useMemo(function () {
        var _a;
        if (!channelPriceData)
            return { raw: "无", roomCount: 0, rawRoomNames: [] };
        var priceData = channelPriceData;
        var list = (_a = priceData === null || priceData === void 0 ? void 0 : priceData.data) === null || _a === void 0 ? void 0 : _a.roomTypeList;
        if (!Array.isArray(list))
            return { raw: "有", roomCount: 0, rawRoomNames: [] };
        return {
            raw: "有",
            roomCount: list.length,
            rawRoomNames: list.map(function (rt) { return rt.roomTypeName; }),
        };
    }, [channelPriceData]);
    var diagChart = useMemo(function () {
        var _a, _b, _c;
        if (!chartData)
            return { chart: "空", categories: 0, klineLen: 0 };
        var kline = (_a = chartData === null || chartData === void 0 ? void 0 : chartData.dataset) === null || _a === void 0 ? void 0 : _a.klineData;
        var cats = (_c = (_b = chartData === null || chartData === void 0 ? void 0 : chartData.categories) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
        return { chart: "有", categories: cats, klineLen: Array.isArray(kline) ? kline.length : 0 };
    }, [chartData]);
    useEffect(function () {
        if (!isDev || storeId == null)
            return;
        var conclusion;
        if (diagRaw.raw === "无") {
            conclusion = "【没取到】原始数据为空";
        }
        else if (diagChart.chart === "空") {
            conclusion = "【取到了但没渲染】原始有数据，图表转换后为空（检查房型名是否匹配）";
        }
        else {
            conclusion = "【正常】有数据且图表有内容";
        }
        console.log("[\u6E20\u9053\u623F\u578B\u4EF7\u683C \u8BCA\u65AD] storeId=".concat(storeId), {
            原始数据: diagRaw.raw,
            房型条数: diagRaw.roomCount,
            原始房型名: diagRaw.rawRoomNames,
            当前选中房型: selectedRoomType,
            图表数据: diagChart.chart,
            渠道数: diagChart.categories,
            kline点数: diagChart.klineLen,
            结论: conclusion,
        });
    }, [storeId, diagRaw, diagChart, selectedRoomType]);
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: "\n          .channel-price-header-select .ant-select {\n            width: auto !important;\n            min-width: 120px !important;\n          }\n          .channel-price-header-select .ant-select-selection-item {\n            white-space: nowrap !important;\n            overflow: visible !important;\n            text-overflow: clip !important;\n          }\n          .channel-price-header-select .ant-select-dropdown {\n            min-width: fit-content !important;\n          }\n          .channel-price-header-select .ant-select-dropdown .ant-select-item {\n            white-space: nowrap !important;\n          }\n        " }), _jsxs("div", __assign({ "data-component-id": "chart:channel-price", style: __assign({ display: "flex", flexDirection: "column", width: 400, height: 300, border: "1px solid #f0f0f0", borderRadius: 4, background: "#fafafa", boxSizing: "border-box", overflow: "visible", flexShrink: 0 }, style) }, { children: [_jsxs("div", __assign({ style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fff",
                            flexShrink: 0,
                        } }, { children: [_jsxs("div", __assign({ style: { display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 } }, { children: [titleLeftAdornment && (_jsx("div", __assign({ style: { marginLeft: -4, marginRight: 4, flexShrink: 0, display: "flex", alignItems: "center" } }, { children: titleLeftAdornment }))), onTitleClick ? (_jsx("button", __assign({ onClick: onTitleClick, type: "button", style: {
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: "#333",
                                            cursor: "pointer",
                                            userSelect: "none",
                                            transition: "color 0.3s ease",
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            margin: 0,
                                            textAlign: "left",
                                        }, onMouseEnter: function (e) {
                                            e.currentTarget.style.color = "#1890ff";
                                        }, onMouseLeave: function (e) {
                                            e.currentTarget.style.color = "#333";
                                        }, title: "\u70B9\u51FB\u67E5\u770B\u6E20\u9053\u623F\u4EF7\u5206\u6790" }, { children: "\u6E20\u9053\u623F\u4EF7" }))) : (_jsx("div", __assign({ style: {
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: "#333",
                                            cursor: "default",
                                            userSelect: "none",
                                        } }, { children: "\u6E20\u9053\u623F\u4EF7" })))] })), _jsx("div", __assign({ className: "channel-price-header-select" }, { children: _jsx(Select, { value: selectedRoomType, onChange: setSelectedRoomType, size: "small", options: roomTypeOptions, placeholder: "\u9009\u62E9\u623F\u578B", popupMatchSelectWidth: false }) }))] })), _jsx("div", __assign({ style: {
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            padding: 0,
                            boxSizing: "border-box",
                            overflow: "visible",
                            position: "relative",
                            display: "flex",
                            height: "100%",
                            background: "#fff",
                        } }, { children: hasValidData ? (_jsx(CompsChart, { type: "combo", data: chartData, settings: getChartSettings(), style: { width: "100%", height: "100%" } })) : (_jsx(EmptyDataPlaceholder, { description: "\u6682\u65E0\u6570\u636E" })) }))] }))] }));
};
export default ChannelPrice;
